import React from 'react'
import { Form, Tooltip, theme } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Field as RcField } from 'rc-field-form'
import type { Meta } from 'rc-field-form/es/interface'
import { FormContext } from 'antd/es/form/context'
import { useLocale } from 'antd/es/locale'
import { fontSize, lineHeight, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { boundFieldId, isRequiredByRules, safeFieldId } from './fieldId'
import { FieldGroupContext } from './FieldGroupContext'
import type { FieldControlBag, FieldRenderProps, FieldRequirement } from './types'
import { VISUALLY_HIDDEN_CLASS } from './visuallyHidden'

/**
 * The label, requirement marker, description and message row, plus the wiring that ties them to
 * the control.
 *
 * Binding is opt-in through `name`, never inferred from nesting: React context flows through
 * portals, so a modal field inside a page-level `<Form>` would otherwise bind to that page's form.
 * `Form.Item` is never mounted, so `labelCol`/`wrapperCol`/`colon` on a parent `<Form>` do not
 * reach a `Field`.
 */
export function Field(props: FieldRenderProps) {
  const { name, rules, valuePropName, trigger } = props
  const form = Form.useFormInstance()

  if (name !== undefined && form) {
    return (
      <RcField name={name} rules={rules} valuePropName={valuePropName} trigger={trigger}>
        {(control, meta) => <FieldChrome {...props} control={control as FieldControlBag} meta={meta} />}
      </RcField>
    )
  }

  return <FieldChrome {...props} />
}

interface FieldChromeProps extends FieldRenderProps {
  control?: FieldControlBag
  meta?: Meta
}

function FieldChrome({
  label,
  hideLabel = false,
  labelAs = 'control',
  ariaLabel,
  description,
  tooltip,
  requirement,
  error,
  warning,
  name,
  rules,
  disabled,
  id: idOverride,
  'data-qa': dataQaAttr,
  style,
  className,
  children,
  control,
  meta,
}: FieldChromeProps) {
  // Theme tokens, never the static colour literals: those are light-theme values and measure
  // 1.43:1 on a dark surface.
  const { token } = theme.useToken()
  const { name: formName, requiredMark } = React.useContext(FormContext)
  const group = React.useContext(FieldGroupContext)
  const [formLocale] = useLocale('Form')
  const reactId = React.useId()

  const id = idOverride ?? (name !== undefined ? boundFieldId(name, formName) : undefined) ?? safeFieldId(reactId)
  const labelId = `${id}-label`
  const descriptionId = `${id}-description`
  const messageId = `${id}-message`

  const resolvedRequirement: FieldRequirement = requirement ?? (isRequiredByRules(rules) ? 'required' : 'none')

  // The caller's message beats a rule's: a server rejection is newer than the last client pass.
  const message = error ?? meta?.errors[0] ?? warning ?? meta?.warnings[0]
  const invalid = Boolean(error ?? meta?.errors.length)

  // Inside a group the group owns the message row: one label over several controls has one error.
  const showsOwnMessage = !group && message !== undefined
  const describedBy = [description !== undefined ? descriptionId : undefined, showsOwnMessage ? messageId : group?.messageId].filter(Boolean).join(' ')

  const bag: FieldControlBag = {
    ...control,
    id,
    'aria-describedby': describedBy || undefined,
    'aria-invalid': invalid || group?.invalid ? true : undefined,
    'aria-required': resolvedRequirement === 'required' ? true : undefined,
    'aria-label': label === undefined ? ariaLabel : undefined,
    'aria-labelledby': label !== undefined && labelAs === 'group' ? labelId : undefined,
    'data-qa': dataQaAttr,
    disabled,
  }

  const showOptionalMark = requiredMark !== false && resolvedRequirement === 'optional'

  const labelClass = hideLabel ? VISUALLY_HIDDEN_CLASS : undefined
  const labelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxs,
    fontSize: fontSize.base,
    lineHeight: lineHeight.base,
    color: token.colorText,
  }
  const labelContent = (
    <>
      {label}
      {/* colorTextDescription, not colorTextTertiary: the tertiary alias measures 3.36:1. */}
      {showOptionalMark && <span style={{ color: token.colorTextDescription }}>{formLocale?.optional}</span>}
      {tooltip !== undefined && (
        <Tooltip title={tooltip}>
          <QuestionCircleOutlined aria-hidden style={{ color: token.colorTextSecondary }} />
        </Tooltip>
      )}
    </>
  )

  // No outer margin: the gap between fields belongs to the parent FieldStack.
  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xxs, ...style }}>
      {label !== undefined &&
        (labelAs === 'group' ? (
          // A span, not a label: `htmlFor` can only point at one control.
          <span id={labelId} className={labelClass} style={labelStyle}>
            {labelContent}
          </span>
        ) : (
          <label id={labelId} htmlFor={id} className={labelClass} style={labelStyle}>
            {labelContent}
          </label>
        ))}

      {children(bag)}

      {description !== undefined && (
        <div id={descriptionId} style={{ fontSize: fontSize.sm, lineHeight: lineHeight.base, color: token.colorTextDescription }}>
          {description}
        </div>
      )}

      {/* antd's own ErrorList carries neither, so an error is otherwise only conveyed on focus.
          Polite, not assertive, because validation fires while the user is still typing. */}
      {showsOwnMessage && (
        <div
          id={messageId}
          role="alert"
          aria-live="polite"
          // The *Text variants, never colorError/colorWarning: those are filled-button colours
          // and measure 3.27:1 and 1.90:1 on white.
          style={{
            fontSize: fontSize.sm,
            lineHeight: lineHeight.base,
            color: invalid ? token.colorErrorText : token.colorWarningText,
          }}
        >
          {message}
        </div>
      )}
    </div>
  )
}

export default Field
